import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { REPORT_PERIOD_LABELS, REPORT_PERIODS, type ResolvedReportFilters } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { DatePicker } from '../../../components/DatePicker';
import { Field } from '../../../components/Field';
import { Select, type SelectOption } from '../../../components/Select';
import { useUsers } from '../../admin/api/users-api';
import { useDepartments, useVentures } from '../../organisation/api/organisation-api';
import { urlOf, type ReportUrl } from '../api/reports-api';
import styles from '../Reports.module.css';

const ALL = '';
const RECRUITER_ROLE = /recruiter/i;
const USER_PAGE = 200;

/**
 * F17.03 — venture, department, date range, recruiter. Every change goes straight into the
 * address; the figures below swap in place when the new ones arrive.
 */
export function ReportFilters({
  filters,
  onChange,
  isRecruiterHidden = false,
  extra,
  isExtraActive = false,
  onClear,
}: {
  readonly filters: ResolvedReportFilters;
  readonly onChange: (next: ReportUrl) => void;
  /** A recruiter's reports are their own records (§L23), so the choice is not theirs to make. */
  readonly isRecruiterHidden?: boolean;
  /** S29's own filters (role family, source), placed in the same bar. */
  readonly extra?: ReactNode;
  readonly isExtraActive?: boolean;
  readonly onClear?: () => void;
}): ReactNode {
  const ventures = useVentures();
  const departments = useDepartments();
  // The recruiter list comes from user management, which only the HR Lead may read.
  const users = useUsers({ page: 1, pageSize: USER_PAGE, status: 'account_active' }, !isRecruiterHidden);
  const current = urlOf(filters);
  const recruiters = (users.data?.rows ?? []).filter((user) => RECRUITER_ROLE.test(user.roleLabels));
  const inVenture = (departments.data ?? []).filter((department) => !department.archivedAt && (!filters.ventureId || department.ventureId === filters.ventureId));
  const isFiltered = filters.ventureId !== null || filters.departmentId !== null || (!isRecruiterHidden && filters.recruiterUserId !== null) || isExtraActive;

  const set = (patch: ReportUrl): void => onChange(withoutEmpty({ ...current, ...patch }));
  const setPeriod = (value: string): void => {
    const period = REPORT_PERIODS.find((option) => option === value);
    if (!period) return;
    set(period === 'custom' ? { period, from: filters.from, to: filters.to } : { period, from: ALL, to: ALL });
  };

  return (
    <div className={styles.filters} role="group" aria-label="Filters">
      <Filter label="Period" value={filters.period} options={REPORT_PERIODS.map((period) => ({ value: period, label: REPORT_PERIOD_LABELS[period] }))} onChange={setPeriod} />
      {filters.period === 'custom' && (
        <>
          <div className={styles.filter}>
            <Field label="From">{({ inputId }) => <DatePicker id={inputId} label="From" value={filters.from} onChange={(from) => set({ period: 'custom', from, to: filters.to })} />}</Field>
          </div>
          <div className={styles.filter}>
            <Field label="To">{({ inputId }) => <DatePicker id={inputId} label="To" value={filters.to} min={filters.from} onChange={(to) => set({ period: 'custom', from: filters.from, to })} />}</Field>
          </div>
        </>
      )}
      <Filter
        label="Venture"
        value={filters.ventureId ?? ALL}
        options={[{ value: ALL, label: 'All ventures' }, ...(ventures.data ?? []).filter((venture) => !venture.archivedAt).map((venture) => ({ value: venture.id, label: venture.name }))]}
        onChange={(ventureId) => set({ ventureId, departmentId: ALL })}
      />
      <Filter
        label="Department"
        value={filters.departmentId ?? ALL}
        options={[{ value: ALL, label: 'All departments' }, ...inVenture.map((department) => ({ value: department.id, label: department.name }))]}
        onChange={(departmentId) => set({ departmentId })}
      />
      {!isRecruiterHidden && (
        <Filter
          label="Recruiter"
          value={filters.recruiterUserId ?? ALL}
          options={[{ value: ALL, label: 'All recruiters' }, ...recruiters.map((user) => ({ value: user.id, label: user.fullName }))]}
          onChange={(recruiterUserId) => set({ recruiterUserId })}
        />
      )}
      {extra}
      {isFiltered && (
        <Button variant="ghost" size="sm" icon={X} onClick={onClear ?? (() => onChange({ period: filters.period, ...(filters.period === 'custom' ? { from: filters.from, to: filters.to } : {}) }))}>
          Clear filters
        </Button>
      )}
    </div>
  );
}

/** One labelled filter: the label is visible and names the control (06-qol §7). */
export function Filter({ label, value, options, onChange }: { readonly label: string; readonly value: string; readonly options: readonly SelectOption[]; readonly onChange: (value: string) => void }): ReactNode {
  return (
    <div className={styles.filter}>
      <Field label={label}>{({ inputId }) => <Select id={inputId} label={label} value={value} options={options} onChange={onChange} />}</Field>
    </div>
  );
}

/** "All …" is the absence of a filter, so it leaves the address. */
function withoutEmpty(url: ReportUrl): ReportUrl {
  const keep = (value: string | undefined): value is string => value !== undefined && value !== ALL;
  return {
    ...(url.period ? { period: url.period } : {}),
    ...(keep(url.from) ? { from: url.from } : {}),
    ...(keep(url.to) ? { to: url.to } : {}),
    ...(keep(url.ventureId) ? { ventureId: url.ventureId } : {}),
    ...(keep(url.departmentId) ? { departmentId: url.departmentId } : {}),
    ...(keep(url.recruiterUserId) ? { recruiterUserId: url.recruiterUserId } : {}),
  };
}
